import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaFacebook, FaInstagram, FaPaperPlane, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        organization: '',
        serviceType: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        await new Promise(resolve => setTimeout(resolve, 1000));

        const message = `Hi! I'm ${formData.name} from ${formData.organization || 'N/A'}.

Email: ${formData.email}
Phone: ${formData.phone}
Service Interest: ${formData.serviceType || 'General Inquiry'}

Message: ${formData.message}`;

        const whatsappUrl = `https://wa.me/919059651065?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');

        toast.success('Redirecting to WhatsApp...');
        setIsSubmitting(false);
        setFormData({ name: '', email: '', phone: '', organization: '', serviceType: '', message: '' });
    };

    const contactInfo = [
        {
            icon: FaPhone,
            label: 'Phone',
            value: '+91 90596 51065',
            description: 'Call us for immediate assistance',
            href: 'tel:+919059651065',
            action: 'Call Now',
        },
        {
            icon: FaEnvelope,
            label: 'Email',
            value: 'goprezzpvt@gmail.com',
            description: 'Send us your queries anytime',
            href: 'mailto:goprezzpvt@gmail.com',
            action: 'Send Email',
        },
        {
            icon: FaWhatsapp,
            label: 'WhatsApp',
            value: '+91 90596 51065',
            description: 'Quick response via WhatsApp',
            href: 'https://wa.me/919059651065',
            action: 'Chat Now',
        },
        {
            icon: FaMapMarkerAlt,
            label: 'Location',
            value: 'Hyderabad, Telangana',
            description: 'Serving across the city',
            href: null,
            action: null,
        },
    ];

    const services = [
        'Daily Car Cleaning',
        'Steam Ironing Service',
        'Wardrobe Care',
        'Shoe Care',
        'Parking & Basement Services',
        'Custom Package',
    ];

    const faqs = [
        {
            question: 'How do I book a service?',
            answer: 'You can book any service by filling out the form on this page, calling us directly, or sending a WhatsApp message. We respond within minutes!',
        },
        {
            question: 'What areas do you serve?',
            answer: 'We currently serve all major residential areas in Hyderabad including Gachibowli, Madhapur, Kondapur, HITEC City, Jubilee Hills, Banjara Hills, and more.',
        },
        {
            question: 'Do you offer monthly packages?',
            answer: 'Yes! We offer convenient monthly subscription packages for all our services with significant savings compared to on-demand pricing.',
        },
        {
            question: 'Are your staff background-verified?',
            answer: 'Absolutely. All our staff members undergo thorough background verification and professional training before they start serving customers.',
        },
        {
            question: 'Can you service our entire apartment community?',
            answer: 'Yes, we specialize in community partnerships. We offer customized packages and dedicated staff for apartment complexes and gated communities.',
        },
    ];

    return (
        <div className="min-h-screen pt-20 bg-white">
            <Toaster position="top-right" />

            {/* Hero Section */}
            <section className="section-padding bg-gray-50 relative overflow-hidden">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary-200 rounded-full blur-3xl opacity-20"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent-200 rounded-full blur-3xl opacity-20"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-6">
                            Get In Touch
                        </span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                            Partner
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> With Us</span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                            We collaborate with communities, organizations, and individuals to bring
                            convenient, reliable, and eco-friendly services closer to people.
                        </p>
                    </motion.div>

                    {/* Contact Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={info.label}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white p-6 rounded-2xl text-center group border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                                <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                    <info.icon className="w-6 h-6 text-primary-600" />
                                </div>
                                <h3 className="text-gray-900 font-semibold mb-1">{info.label}</h3>
                                <p className="text-primary-600 font-medium mb-1">{info.value}</p>
                                <p className="text-gray-500 text-sm mb-4">{info.description}</p>
                                {info.href && (
                                    <a
                                        href={info.href}
                                        target={info.href.startsWith('http') ? '_blank' : undefined}
                                        rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                        className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                                    >
                                        {info.action}
                                        <FaArrowRight className="w-3 h-3" />
                                    </a>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form Section */}
            <section className="section-padding bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100">
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                    Send Us a Message
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Fill out the form below and we'll get back to you within 24 hours.
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">Your Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="John Doe"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">Phone Number *</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+91 XXXXX XXXXX"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="your@email.com"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">Community / Organization</label>
                                            <input
                                                type="text"
                                                name="organization"
                                                value={formData.organization}
                                                onChange={handleChange}
                                                placeholder="Apartment name or company"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 text-sm font-medium mb-2">Service You're Interested In</label>
                                        <select
                                            name="serviceType"
                                            value={formData.serviceType}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all cursor-pointer"
                                        >
                                            <option value="">Select a service</option>
                                            {services.map((service) => (
                                                <option key={service} value={service}>{service}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 text-sm font-medium mb-2">Tell us more about your needs *</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Describe your requirements, number of cars, community size, etc..."
                                            rows={5}
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all resize-none"
                                            required
                                        />
                                    </div>

                                    <motion.button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="btn-primary w-full flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane />
                                                Send Message
                                            </>
                                        )}
                                    </motion.button>
                                </form>
                            </div>
                        </motion.div>

                        {/* Additional Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            {/* Quick WhatsApp */}
                            <a
                                href="https://wa.me/919059651065"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-8 rounded-2xl bg-gradient-to-r from-green-50 to-green-100 border border-green-200 hover:shadow-lg transition-all group"
                            >
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-xl bg-green-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-green-500/30">
                                        <FaWhatsapp className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-gray-900 font-bold text-xl">Prefer WhatsApp?</p>
                                        <p className="text-gray-600">Click here to chat directly. Quick response guaranteed!</p>
                                    </div>
                                </div>
                            </a>

                            {/* Business Hours */}
                            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-lg">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                                    <FaClock className="text-primary-600" />
                                    Business Hours
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Monday - Friday</span>
                                        <span className="text-gray-900 font-medium">9:00 AM - 6:00 PM</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Saturday</span>
                                        <span className="text-gray-900 font-medium">9:00 AM - 6:00 PM</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-gray-600">Sunday</span>
                                        <span className="text-red-500 font-medium">Holiday</span>
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm mt-4">
                                    * Service timings may vary. Contact us for specific availability.
                                </p>
                            </div>

                            {/* Social Links */}
                            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-lg">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Follow Us</h3>
                                <p className="text-gray-600 mb-6">Stay connected for updates, tips, and more!</p>
                                <div className="flex gap-4">
                                    <a
                                        href="https://www.facebook.com/share/17oPmVW7PS/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white transition-all border border-blue-100"
                                    >
                                        <FaFacebook className="w-6 h-6" />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/goprezzpvt"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-14 h-14 rounded-xl bg-pink-50 flex items-center justify-center text-pink-600 hover:bg-pink-600 hover:text-white transition-all border border-pink-100"
                                    >
                                        <FaInstagram className="w-6 h-6" />
                                    </a>
                                    <a
                                        href="https://wa.me/919059651065"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center text-green-600 hover:bg-green-600 hover:text-white transition-all border border-green-100"
                                    >
                                        <FaWhatsapp className="w-6 h-6" />
                                    </a>
                                </div>
                            </div>

                            {/* Service Areas */}
                            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-lg">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                                    <FaMapMarkerAlt className="text-primary-600" />
                                    Areas We Serve
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {['Gachibowli', 'Madhapur', 'HITEC City', 'Kondapur', 'Jubilee Hills', 'Banjara Hills', 'Kukatpally', 'Miyapur', 'Manikonda', 'Nanakramguda'].map((area) => (
                                        <span key={area} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                                            {area}
                                        </span>
                                    ))}
                                    <span className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium">
                                        + More areas
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="section-padding bg-gray-50">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-4">
                            FAQ
                        </span>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
                            Frequently Asked
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D]"> Questions</span>
                        </h2>
                    </motion.div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <motion.div
                                key={faq.question}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                            >
                                <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-start gap-3">
                                    <FaCheckCircle className="w-5 h-5 text-primary-500 mt-1 flex-shrink-0" />
                                    {faq.question}
                                </h4>
                                <p className="text-gray-600 ml-8">{faq.answer}</p>
                            </motion.div>
                        ))}
                    </div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center text-gray-500 mt-8"
                    >
                        Have more questions? <a href="https://wa.me/919059651065" target="_blank" rel="noopener noreferrer" className="text-primary-600 font-semibold hover:underline">Chat with us on WhatsApp</a>
                    </motion.p>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
